/**
 * PermissionAuditLog Component
 *
 * Display permission change history with filtering, pagination, and export.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Filter,
  Download,
  Search,
  Calendar,
  RefreshCw,
  X,
  AlertCircle,
  CheckCircle,
  XCircle,
  Shield,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import {
  superAdminApi,
  PermissionAuditEntry,
  PermissionAuditQueryParams,
  AdminUser,
} from '../../services/adminApi';
import { Pagination } from './Pagination';

interface PermissionAuditLogProps {
  userId?: number;
  permissionKey?: string;
  showFilters?: boolean;
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  grant: <CheckCircle className="w-4 h-4 text-green-600" />,
  revoke: <XCircle className="w-4 h-4 text-red-600" />,
  role_assigned: <UserPlus className="w-4 h-4 text-blue-600" />,
  role_removed: <UserMinus className="w-4 h-4 text-orange-600" />,
};

const ACTION_LABELS: Record<string, string> = {
  grant: 'Granted',
  revoke: 'Revoked',
  role_assigned: 'Role Assigned',
  role_removed: 'Role Removed',
};

const ACTION_COLORS: Record<string, string> = {
  grant: 'bg-green-100 text-green-800',
  revoke: 'bg-red-100 text-red-800',
  role_assigned: 'bg-blue-100 text-blue-800',
  role_removed: 'bg-orange-100 text-orange-800',
};

export const PermissionAuditLog: React.FC<PermissionAuditLogProps> = ({
  userId: propUserId,
  permissionKey: propPermissionKey,
  showFilters = true,
}) => {
  const [auditEntries, setAuditEntries] = useState<PermissionAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Filters
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>(propUserId);
  const [permissionKeyFilter, setPermissionKeyFilter] = useState<string>(propPermissionKey || '');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params: PermissionAuditQueryParams = {
      page: currentPage,
      limit: itemsPerPage,
    };

    if (userIdFilter) params.user_id = userIdFilter;
    if (permissionKeyFilter) params.permission_key = permissionKeyFilter;
    if (actionFilter) params.action = actionFilter as 'grant' | 'revoke' | 'role_assigned' | 'role_removed';
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    try {
      const response = await superAdminApi.getPermissionAuditLog(params);
      setAuditEntries(response.data);
      setTotalEntries(response.total);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
      setAuditEntries([]);
      setTotalEntries(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, userIdFilter, permissionKeyFilter, actionFilter, startDate, endDate]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  // Clear filters
  const handleClearFilters = () => {
    if (!propUserId) setUserIdFilter(undefined);
    if (!propPermissionKey) setPermissionKeyFilter('');
    setActionFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters =
    (userIdFilter && !propUserId) ||
    (permissionKeyFilter && !propPermissionKey) ||
    actionFilter ||
    startDate ||
    endDate;

  // Export to CSV
  const handleExportCSV = () => {
    if (auditEntries.length === 0) return;

    const headers = [
      'ID',
      'Date/Time',
      'Action',
      'User',
      'Permission',
      'Performed By',
      'Reason',
      'Metadata',
    ];

    const rows = auditEntries.map((entry) => [
      entry.id,
      formatDateTime(entry.created_at),
      ACTION_LABELS[entry.action] || entry.action,
      entry.subject_email || `User ${entry.subject_id}`,
      entry.permission_key,
      entry.performer_email || `User ${entry.performed_by}`,
      entry.reason || 'N/A',
      entry.metadata ? JSON.stringify(entry.metadata) : 'N/A',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `permission-audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalPages = Math.ceil(totalEntries / itemsPerPage);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Permission Audit Log</h3>
          <span className="text-sm text-gray-500">({totalEntries} entries)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAuditLog}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {showFilters && (
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilterPanel || hasActiveFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  {[userIdFilter && !propUserId, permissionKeyFilter && !propPermissionKey, actionFilter, startDate, endDate].filter(Boolean).length}
                </span>
              )}
            </button>
          )}
          <button
            onClick={handleExportCSV}
            disabled={auditEntries.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && showFilterPanel && (
        <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* User ID Filter */}
            {!propUserId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                <input
                  type="number"
                  value={userIdFilter || ''}
                  onChange={(e) => setUserIdFilter(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Filter by user ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            )}

            {/* Permission Filter */}
            {!propPermissionKey && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission</label>
                <input
                  type="text"
                  value={permissionKeyFilter}
                  onChange={(e) => setPermissionKeyFilter(e.target.value)}
                  placeholder="e.g., carriers.export"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            )}

            {/* Action Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All Actions</option>
                <option value="grant">Grant</option>
                <option value="revoke">Revoke</option>
                <option value="role_assigned">Role Assigned</option>
                <option value="role_removed">Role Removed</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="mt-4 flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4" />
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Audit Log Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : auditEntries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">
            {hasActiveFilters ? 'No audit entries match your filters' : 'No audit entries found'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date/Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permission
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performed By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(entry.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${ACTION_COLORS[entry.action]}`}>
                        {ACTION_ICONS[entry.action]}
                        {ACTION_LABELS[entry.action] || entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {entry.subject_email || (
                        <span className="text-gray-500">User #{entry.subject_id}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{entry.permission_key}</code>
                      {entry.metadata?.expires_at && (
                        <div className="text-xs text-gray-500 mt-1">
                          Expires: {formatDate(entry.metadata.expires_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {entry.performer_email || (
                        <span className="text-gray-500">User #{entry.performed_by}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                      {entry.reason || <span className="text-gray-400 italic">No reason provided</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalEntries > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalEntries}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};
